import * as vp from 'vopay';
import {
  Box,
  Card,
  Title,
  Table,
  Pagination,
  Icon,
  usePagination,
  useUpdateQueryParams,
  GetColorStatus,
  capitalizeFirstLetter,
  getMaskedNumber,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import styles from './BankVerifyHistory.module.scss';
import { format, parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import useFetchAccountVerification from '@/hooks/useApi/useFetchAccountVerification';

import BankVerifyHistoryForm from './BankVerifyHistoryForm';
import useFileDownload from '@/hooks/useFiledDownload';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

import BankVerifyHistoryExport from './BankVerifyHistoryExport';
import { Button, TableFiltersContainer, TransactionStatus } from 'UI';
import { withStyles } from 'vopay';

const cn = withStyles(styles);

type QueryParams = {
  requestStatus: string;
  verificationStatus: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
};

type BankVerifySearchParams = {
  requestStatus?: string;
  verificationStatus?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  timeZone?: string;
};

type FC = FunctionComponent;

const BankVerifyHistory: FC = () => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();

  const history = useHistory();
  const location = useLocation();
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'BankVerifyHistory' });
  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      timeZone: params.get('timeZone') || '',
      requestStatus: params.get('requestStatus') || '',
      verificationStatus: params.get('verificationStatus') || '',
      searchTerm: params.get('searchTerm') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);
  const timeZone = useMemo(
    () => (queryParams.timeZone as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );
  const requestStatus = useMemo(() => queryParams.requestStatus || '', [queryParams]);
  const verificationStatus = useMemo(() => queryParams.verificationStatus || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const startDate = useMemo(() => queryParams.startDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.endDate || '', [queryParams]);

  const timeDifference = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const filtersApplied = useMemo(() => {
    return Boolean(
      requestStatus ||
        verificationStatus ||
        searchTerm ||
        (timeZone && timeZone !== (userSettings.timezone || 'UTC')) ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [
    requestStatus,
    verificationStatus,
    searchTerm,
    timeZone,
    userSettings.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [filteredList, setFilteredList] = useState<vp.VerificationRequestsList[]>([]);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [verificationHistory, , , fetchVerificationHistory] = useFetchAccountVerification({});
  const [verificationHistoryExport, , , fetchVerificationHistoryExport] = useFetchAccountVerification({});
  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return verificationHistory.PaginationMetaData?.TotalRecords || 0;
  }, [verificationHistory.VerificationRequests]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: BankVerifySearchParams = {}) => {
    const defaultValues = {
      StartDateTime: searchParams.timeRange?.[0]
        ? format(searchParams.timeRange?.[0], 'yyyy-MM-dd')
        : startDate || timeDifference,
      EndDateTime: searchParams.timeRange?.[1]
        ? format(searchParams.timeRange?.[1], 'yyyy-MM-dd')
        : endDate || currentDateTime,
      Timezone: searchParams.timeZone ?? timeZone,
      ...(searchParams.requestStatus === 'all' ? {} : { RequestStatus: searchParams.requestStatus }),
      SearchText: searchParams.searchTerm ?? searchTerm,
      ...(searchParams.verificationStatus === 'all' ? {} : { VerificationStatus: searchParams.verificationStatus }),
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }
    try {
      const res = await fetchVerificationHistory(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const verificationRequest = Object.values(res.VerificationRequests || {});
      setFilteredList(verificationRequest);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching eLinx history';
      alertDialog.error('Error', msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const VerificationRequests = Object.values(verificationHistory.VerificationRequests || {}).reverse();
    setFilteredList(VerificationRequests);
  }, [verificationHistory.VerificationRequests]);

  useEffect(() => {
    const offset = setPageIndex(0);

    const searchParams = {
      requestStatus,
      verificationStatus,
      searchTerm,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
      timeZone: timeZone,
    };

    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const defaultValues = {
      StartDateTime: timeDifference,
      EndDateTime: currentDateTime,
      Timezone: timeZone,
      RequestStatus: requestStatus,
      SearchText: searchTerm,
      VerificationStatus: verificationStatus,
    };
    const bankVerifyHistoryRequest = defaultValues;
    bankVerifyHistoryRequest.StartDateTime = startDate;
    bankVerifyHistoryRequest.EndDateTime = endDate;
    const exportBankVerifyData = await fetchVerificationHistoryExport(bankVerifyHistoryRequest);
    switch (exportType) {
      case 'pdf':
        downloadPdfTable('export-bank-verify-history-table', t('title'));
        break;
      case 'csv':
        downloadCsvFile('export-bank-verify-history-table', t('title'));
        break;
      case 'xls':
        downloadXls('export-bank-verify-history-table', t('title'));
        break;
      case 'doc':
        downloadDoc('export-bank-verify-history-table', t('title'));
        break;
      case 'text':
        downloadTxt('export-bank-verify-history-table', t('title'));
        break;
      case 'json':
        downloadJson(Object.values(exportBankVerifyData.VerificationRequests || {}), t('title'));
        break;
      case 'xml':
        downloadXml('export-bank-verify-history-table', t('title'));
        break;
      case 'print':
        printFile('export-bank-verify-history-table');
        break;
      default:
        break;
    }
  };

  const tableProps: vp.TableProps<vp.VerificationRequestsList> = {
    id: 'bank-verify-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'clientName',
        header: t('Columns.clientName'),
        align: 'left',
        template: ({ data }) => {
          const name =
            data.VerificationType === 'interac'
              ? data.Details.EmailAddress.AccountHolderName
              : `${data.Details.Credit.FirstName} ${data.Details.Credit.LastName}`;
          const email =
            data.VerificationType === 'interac' ? data.Details.EmailAddress.EmailAddress : data.EmailAddress.Receiver;

          return (
            <Box flx="col" ai="fs">
              <div className={cn('clientName')}>{name}</div>
              <div className={cn('clientEmail')}>{email}</div>
            </Box>
          );
        },
      },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) => (
          <Box ai="center">
            {data.VerificationType === 'bank' && (
              <>
                {data.Details.Bank.FinancialInstitutionId !== null &&
                  data.Details.Bank.FinancialInstitutionId !== '' &&
                  vp.hasBankImage(data.Details.Bank.FinancialInstitutionId) && (
                    <img
                      src={`https://embed-dev.vopay.com/images/logos-banks/${data.Details.Bank.FinancialInstitutionId}.svg`}
                      alt="Bank Logo"
                    />
                  )}
                <span>{getMaskedNumber(data.Details.Bank.AccountNumber)}</span>
              </>
            )}

            {data.VerificationType === 'interac' && (
              <>
                <Icon icon="interacColor" size="md" color="none" />
                <span>{data.Details.EmailAddress.EmailAddress}</span>
              </>
            )}
          </Box>
        ),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Request)}
              label={capitalizeFirstLetter(data.Status.Request) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'verificationStatus',
        header: t('Columns.verificationStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Verification)}
              label={capitalizeFirstLetter(data.Status.Verification) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'updatedOn',
        header: t('Columns.updatedOn'),
        align: 'left',
        field: 'LastModified',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status.Request === 'active' && data.Status.Verification === 'unverified' && (
              <Button
                id={`copy-url-${data.VerificationRequestID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl')}
              </Button>
            )}
            <Button
              id={`details-${data.VerificationRequestID}`}
              size="small"
              onClick={() => {
                history.push({
                  pathname: `/bank-verify-history/details/${data.VerificationRequestID}`,
                  state: {
                    verification: data,
                  },
                });
              }}
            >
              {t('details')}
            </Button>
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.VerificationRequestsList } => ({
      id: String(item.VerificationRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns.filter((col) => col.id !== 'view');

  const tableExportProps: vp.TableProps<vp.VerificationRequestsList> = {
    ...tableProps,
    id: 'export-bank-verify-history-table',
    columns: exportColumns,
    rows: Object.values(verificationHistoryExport.VerificationRequests || {}).map(
      (item): { id: string; data: vp.VerificationRequestsList } => ({
        id: String(item.VerificationRequestID || ''),
        data: item,
      }),
    ),
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('noSearchMessage') : t('noRecordMessage');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.verifyTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="row" jc="center">
        <Box flx="col" gapSize="36px" width="100%">
          <Box jc="sb" ai="center" width="100%">
            <Title title={t('title')} />
            <BankVerifyHistoryExport onExport={handleExport} />
          </Box>
          <Card
            style={{
              padding: '16px',
            }}
          >
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <TableFiltersContainer.Trigger />
                <TableFiltersContainer.Content>
                  <BankVerifyHistoryForm
                    requestStatus={requestStatus}
                    timeZone={timeZone}
                    verificationStatus={verificationStatus}
                    searchTerm={searchTerm}
                    initialTimeRange={[
                      parseISO(startDate || timeDifference).getTime() as vp.Timestamp,
                      parseISO(endDate || currentDateTime).getTime() as vp.Timestamp,
                    ]}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    search={(searchParams) => {
                      const offset = setPageIndex(0);

                      updateQueryParams({
                        requestStatus: searchParams.requestStatus || '',
                        verificationStatus: searchParams.verificationStatus || '',
                        searchTerm: searchParams.searchTerm || '',
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        timeZone: searchParams.timeZone || '',
                      });
                      search(offset, searchParams);
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <div style={{ display: 'none' }}>
              <Table {...tableExportProps} />
            </div>
            <Box>
              <Table {...tableProps} />
            </Box>
            <EmptyOrNoResults />
            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    const offset = setPageIndex(e.selected);
                    const searchParams = {
                      requestStatus,
                      verificationStatus,
                      searchTerm,
                      startDate: startDate || timeDifference,
                      endDate: endDate || currentDateTime,
                      timeZone: timeZone,
                    };
                    search(offset, searchParams);
                  }}
                />
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default BankVerifyHistory;
